import { memo, useRef, useMemo, useReducer, useCallback, Key, useState, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ProForm, ProFormCheckbox, ProFormText, ProFormTextArea } from '@ant-design/pro-components';
import { Alert, Button, Flex, Select, Tooltip } from 'antd';
import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  SortableList,
  notification,
  Modal,
  rejectionError,
  IconButton,
} from '@ohpen/enso-ui';
import {
  ActionEntityType,
  CreateExportTemplateRequest,
  ExportTemplateResponse,
  createExportTemplate,
  updateExportTemplate,
} from '@pmp/api';
import { useFeatureToggle } from '@pmp/hooks';
import { actionEntityTypeToTranslationKey } from '@pmp/utils';
import { useAdditionalFieldsStore, useTableViewsStore } from '@pmp/stores';

import { ExportCrudProps } from '../interfaces';
import styles from '../styles.module.css';
import { getColumnsSchema } from '../utils';
import { initialState, reducer } from '../reducer';

const enableCopyViewsByEntity: ActionEntityType[] = [ActionEntityType.PRODUCTS];

export const ExportTemplatesCrudForm = memo(({ entityType, mode, record, onClose, refreshTable }: ExportCrudProps): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const enableAdditionalFields = useFeatureToggle(['enableAdditionalFields']);
  const { getAdditionalFields } = useAdditionalFieldsStore();
  const { getTableViews } = useTableViewsStore();
  const [forceUpdate, setForceUpdate] = useState(false);
  const { t } = useTranslation();
  const isReadOnly = mode === CrudMode.VIEW;

  useEffect(() => {
    if (!mode || !entityType) {
      notification().error({ message: t('global.errors.unexpected') });
      onClose();
      return;
    }

    const entityColumns = getColumnsSchema(entityType).map(column => column.key as Key);
    const defaultOrder = entityColumns.filter(key => key !== 'actions');
    const missingColumns = defaultOrder.filter(column => !record?.columnOrder?.includes(column as string));
    const dataColumnOrder = record?.columnOrder ?? [];

    getAdditionalFields(entityType)
      .then(additionalFields => {
        const additionalFieldsArray = Object.keys(additionalFields ?? {});
        const missingAdditionalFields = additionalFieldsArray.filter(field => !dataColumnOrder.includes(field));
        const columnOrder = [...dataColumnOrder, ...missingColumns, ...(enableAdditionalFields ? missingAdditionalFields : [])];
        const additionalColumns = additionalFieldsArray.reduce((acc, key) => ({ ...acc, [key]: key }), {});
        const columnMapping = enableAdditionalFields
          ? { ...(record?.columnMapping ?? {}), ...additionalColumns }
          : (record?.columnMapping ?? {});

        dispatch({
          type: 'open',
          data: { ...record, columnOrder, columnMapping, entityType: { key: entityType } } as ExportTemplateResponse,
          additionalFields,
          additionalFieldsArray,
        });
      })
      .catch(() => {
        notification().error({ message: t('global.errors.unexpected') });
        onClose();
      });
  }, [mode, enableAdditionalFields, entityType, getAdditionalFields, onClose, record, t]);

  const onConfirm = useCallback(
    async (values: CreateExportTemplateRequest): Promise<void> => {
      if (!state.data || !entityType) return Promise.reject();
      if (mode === CrudMode.VIEW) return Promise.resolve();
      if (mode === CrudMode.CREATE || mode === CrudMode.CLONE) {
        // If columnOrder is [] it means that the user didn't change the order of the columns and as the initial value is forced to be [] to solve the problem with persisted data when the CRUD is closed and opened again, we need to set the columnOrder to the real  initial value from the state.
        if (!values.columnOrder?.length) values.columnOrder = state.data?.columnOrder ?? [];
        if (!values.name || !values.description || !values.columnOrder?.length)
          return Promise.reject(rejectionError(t('global.errors.invalidData'), values));

        const data: CreateExportTemplateRequest = {
          name: values.name,
          description: values.description,
          columnOrder: values.columnOrder,
          columnMapping: values.columnMapping ?? {},
          columnVisibility: values.columnVisibility ?? {},
          entityType: values.entityType,
        };

        return createExportTemplate({ body: data }).then(() => {
          notification().success({ message: t('global.notifications.xCreatedSuccessfully', { x: t('global.labels.exportTemplate') }) });
          refreshTable();
        });
      }
      if (mode === CrudMode.EDIT) {
        // If columnOrder is [] it means that the user didn't change the order of the columns and as the initial value is forced to be [] to solve the problem with persisted data when the CRUD is closed and opened again, we need to set the columnOrder to the real  initial value from the state.
        if (!values.columnOrder?.length) values.columnOrder = state.data?.columnOrder ?? [];
        if (!state.data?.id || state.data?.versionNumber === undefined || !values.name)
          return Promise.reject(rejectionError(t('global.errors.invalidData'), { state, values }));
        const data: CreateExportTemplateRequest = {
          name: values.name,
          description: values.description,
          columnOrder: values.columnOrder,
          columnMapping: values.columnMapping ?? {},
          columnVisibility: values.columnVisibility ?? {},
          entityType: values.entityType,
        };
        return updateExportTemplate({
          path: { templateId: state.data.id },
          query: { version: state.data.versionNumber },
          body: data,
        }).then(() => {
          notification().success({ message: t('global.notifications.xEditedSuccessfully', { x: t('global.labels.exportTemplate') }) });
          refreshTable();
        });
      }
      return Promise.reject(rejectionError(t('global.crud.error.noMode'), state));
    },
    [mode, entityType, refreshTable, state, t],
  );

  const onCopyView = useCallback(async () => {
    if (!entityType) {
      notification().error({ message: t('global.errors.unexpected') });
      return;
    }
    dispatch({ type: 'openViewsModal' });

    const tableViews = await getTableViews(entityType);

    dispatch({ type: 'setTableViews', tableViews });
  }, [getTableViews, entityType]);

  const onChangeSelectView = useCallback((selectedView: string) => {
    dispatch({ type: 'selectView', selectedView });
  }, []);

  const onConfirmViewModal = useCallback(() => {
    const view = state.viewsModal.tableViews?.find(view => view.id === state.viewsModal.selectedView);

    if (!view || !ref.current?.form) {
      notification().error({ message: t('global.crud.error.unexpectedError') });
      return;
    }

    const { columnOrder } = ref.current.form.getFieldsValue(['columnOrder']);
    const newColumnOrder = [
      ...view.order.filter((column: string) => column !== 'actions'),
      ...columnOrder.filter((column: string) => !view.order.includes(column) && !column.includes('customColumn-')),
      ...columnOrder.filter((column: string) => column.includes('customColumn-')),
    ];
    const newVisibility = view.order.reduce((acc, key) => ({ ...acc, [key as string]: view.selected.includes(key) }), {});

    ref?.current.form.setFieldsValue({
      columnOrder: newColumnOrder,
      columnVisibility: newVisibility,
    });
    ref.current.setDisableSave(false);

    setForceUpdate((prev: boolean) => !prev);
    dispatch({ type: 'closeViewsModal' });
  }, [state.viewsModal.selectedView, state.viewsModal.tableViews]);

  const onCancelModal = useCallback(() => {
    dispatch({ type: 'closeViewsModal' });
  }, []);

  const onDragEnd = useCallback((columnOrder: Key[]) => {
    dispatch({ type: 'setColumnOrder', columnOrder: columnOrder as string[] });
    setForceUpdate((prev: boolean) => !prev);
    ref.current?.setDisableSave(false);
  }, []);

  const onAddColumn = useCallback(() => {
    if (!state.data || !ref.current) return;
    const newKey = `customColumn-${Date.now()}`;
    const { columnMapping, columnVisibility } = ref.current.form.getFieldsValue(['columnMapping', 'columnVisibility']);

    dispatch({ type: 'setColumnOrder', columnOrder: [...state.data.columnOrder, newKey] });

    ref.current?.form.setFieldsValue({
      columnMapping: { ...columnMapping, [newKey]: t('global.labels.customColumn') },
      columnVisibility: { ...columnVisibility, [newKey]: true },
    });
    ref.current?.setDisableSave(false);
    setForceUpdate((prev: boolean) => !prev);
  }, [state.data, t]);

  const onDeleteColumn = useCallback(
    (key: string) => {
      if (!ref.current || !state.data) return;
      const newColumnOrder = state.data.columnOrder.filter((column: string) => column !== key);

      // setFieldsValue uses deep-merge and cannot remove keys, so we mutate the raw store directly
      const store = ref.current.form.getFieldsValue(true) as {
        columnMapping?: Record<string, unknown>;
        columnVisibility?: Record<string, unknown>;
      };
      delete store.columnMapping?.[key];
      delete store.columnVisibility?.[key];

      dispatch({ type: 'setColumnOrder', columnOrder: newColumnOrder });
      ref.current.form.setFieldsValue({ columnOrder: newColumnOrder });
      ref.current.setDisableSave(false);
      setForceUpdate((prev: boolean) => !prev);
    },
    [state.data],
  );

  const handleOnClose = useCallback(() => {
    dispatch({ type: 'close' });
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const getRow = useCallback(
    (key: Key) => {
      const isCustom = key.toString().includes('customColumn-');

      function getTitle(): string {
        if (isCustom) return t('global.labels.customColumn');
        if (state.additionalFieldsArray?.includes(key as string)) {
          return state.additionalFields?.[key as string] ?? key.toString();
        }
        return entityType ? t(`pmp.${actionEntityTypeToTranslationKey[entityType]}.labels.${key}`) : key.toString();
      }

      return (
        <ProForm.Group title={getTitle()}>
          <ProFormText
            name={['columnMapping', key]}
            colProps={{ span: 21 }}
            disabled={isReadOnly}
            placeholder={isReadOnly ? '' : undefined}
          />
          <Tooltip placement="topRight" title={t('global.labels.exportThisColumn')}>
            <span>
              <ProFormCheckbox name={['columnVisibility', key]} colProps={{ span: 3 }} disabled={isReadOnly} />
            </span>
          </Tooltip>
          <IconButton type="text" icon={<DeleteOutlined />} onClick={() => onDeleteColumn(key as string)} disabled={!isCustom} />
        </ProForm.Group>
      );
    },
    [isReadOnly, t, state.additionalFieldsArray, entityType, state.additionalFields, onDeleteColumn],
  );

  const viewOptions = useMemo(
    () => state.viewsModal.tableViews?.map(({ id, name }) => ({ value: id, label: name })),
    [state.viewsModal.tableViews],
  );

  const formSchema = useMemo(() => {
    // forceUpdate === undefined never will be true but prevents the linter warning of not needed dependency, but is needed to force the rerender of the form
    if (!state.data || !state.open || forceUpdate === undefined) return null;

    const columnOrder = state.data.columnOrder;
    const rules = [{ required: true, message: t('global.errors.required') }];
    const enableCopyViews = !!entityType && enableCopyViewsByEntity.includes(entityType);

    return (
      <>
        <ProForm.Group title={t('global.labels.general')} collapsible={true}>
          <ProFormText name="entityType" label={t('global.labels.entityType')} disabled={true} />
          {mode === CrudMode.EDIT && <ProFormText name="id" label={t('global.labels.id')} disabled />}
          <ProFormText name="name" label={t('global.labels.label')} rules={rules} disabled={isReadOnly} />
          <ProFormTextArea name="description" label={t('global.labels.description')} rules={rules} disabled={isReadOnly} />
        </ProForm.Group>
        <ProForm.Group title={t('global.labels.exportTemplateMapping')} collapsible={true}>
          <Alert message={t('global.labels.exportTemplateMappingMessage')} type="info" />
          <Flex justify="end">
            {enableCopyViews && (
              <Button size="small" icon={<CopyOutlined />} onClick={onCopyView} disabled={isReadOnly}>
                {t('global.buttons.copyView')}
              </Button>
            )}
          </Flex>
          <SortableList order={columnOrder} onDragEnd={onDragEnd} disabled={isReadOnly}>
            {(key: Key) => getRow(key)}
          </SortableList>

          <Flex justify="end">
            <Button size="small" icon={<PlusOutlined />} onClick={onAddColumn} disabled={isReadOnly}>
              {t('global.labels.addColumn')}
            </Button>
          </Flex>
        </ProForm.Group>
      </>
    );
  }, [state.data, state.open, forceUpdate, t, entityType, mode, isReadOnly, onCopyView, onDragEnd, onAddColumn, getRow]);

  const crudProps: CrudProps<CreateExportTemplateRequest> = useMemo(() => {
    const titles: { [key: string]: string } = {
      [CrudMode.CREATE]: t('global.labels.createX', { x: t('global.labels.exportTemplate') }),
      [CrudMode.CLONE]: t('global.labels.cloneX', { x: t('global.labels.exportTemplate') }),
      [CrudMode.VIEW]: t('global.labels.viewX', { x: t('global.labels.exportTemplate') }),
      [CrudMode.EDIT]: t('global.labels.editX', { x: t('global.labels.exportTemplate') }),
    };
    const confirms: { [key: string]: string } = {
      [CrudMode.CREATE]: t('global.labels.create'),
      [CrudMode.CLONE]: t('global.labels.clone'),
      [CrudMode.EDIT]: t('global.labels.save'),
    };

    return {
      ref,
      open: state.open,
      title: (mode && titles[mode]) ?? '',
      labels: {
        confirm: (mode && confirms[mode]) ?? '',
      },
      initialValues: { ...state.data, columnOrder: [], entityType: entityType } as CreateExportTemplateRequest, // Initial value for columnOrder is forced to be [] to be able to know in useMemo formSchema if we need to initialize columnOrder with the real value from the state to prevent getting undefined or data from previous CRUDs.
      mode,
      groups: true,
      customForm: true,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [t, state.open, state.data, mode, entityType, handleOnClose, onConfirm]);

  return (
    <>
      <Crud {...crudProps}>{formSchema}</Crud>
      <Modal
        title={t('global.labels.selectView')}
        open={state.viewsModal?.open}
        onOk={onConfirmViewModal}
        okButtonProps={{ disabled: !state.viewsModal.selectedView }}
        okText={t('global.buttons.copy')}
        onCancel={onCancelModal}
        zIndex={1001}
        centered={true}
      >
        <Select
          className={styles.select}
          value={state.viewsModal.selectedView}
          options={viewOptions}
          loading={state.viewsModal.loading}
          size="large"
          onChange={onChangeSelectView}
          placeholder={t('global.labels.selectView') + '...'}
        />
      </Modal>
    </>
  );
});
